import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  ArrowLeft,
  BookOpen,
  FileText,
  Loader2,
  Mic,
  MicOff,
  Pause,
  Play,
  RotateCcw,
  Send,
  ShieldCheck,
  SkipBack,
  SkipForward,
  Square,
  Volume2
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { useAuth } from '../contexts/AuthContext';
import { getSourceSet, listSourceSetFiles } from '../services/sourceSetService';
import { getAssistantFailureMessage, getFriendlyErrorMessage } from '../lib/usageLimitMessages';
import { askSourceKnowledge, SourceKnowledgeCitation } from '../services/sourceKnowledgeService';
import {
  clearAskSourceConversation,
  loadAskSourceConversation,
  saveAskSourceTurn
} from '../services/sourceSetAskSessionService';
import { cleanTextForSpeech } from '../lib/utils';
import type { SourceFile, SourceSet } from '../types/sourceSet';

type AskMessage = {
  role: 'user' | 'assistant';
  text: string;
  citations?: SourceKnowledgeCitation[];
  groundingStatus?: string;
};

type AudioStatus = 'idle' | 'playing' | 'paused';

const INITIAL_ASK_SOURCE_MESSAGE: AskMessage = {
  role: 'assistant',
  text: 'Ask a question about this Source Set. I will answer only from the uploaded files and URLs available here.'
};

function getGroundingLabel(status?: string): string {
  switch (status) {
    case 'grounded':
      return 'Grounded';
    case 'partially_grounded':
      return 'Partially grounded';
    case 'insufficient_evidence':
      return 'Insufficient evidence';
    case 'conflicting_sources':
      return 'Conflicting sources';
    default:
      return 'Not checked';
  }
}

function splitAnswerForSpeech(text: string): string[] {
  const cleaned = cleanTextForSpeech(text || '')
    .replace(/\*\*/g, '')
    .replace(/#+\s/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned) return [];

  const rawSegments = cleaned
    .replace(/\b(Step\s+\d+[:.)])/gi, '\n$1')
    .replace(/\b(Safety note[:.)])/gi, '\n$1')
    .replace(/\b(Warning[:.)])/gi, '\n$1')
    .replace(/\b(Source[:.)|Sources[:.)]|Reference[:.)|References[:.)])/gi, '\n$1')
    .split(/\n|(?<=[.!?])\s+/)
    .map((segment) => segment.trim())
    .filter(Boolean);

  const mergedSegments: string[] = [];

  rawSegments.forEach((segment) => {
    if (segment.length <= 360) {
      mergedSegments.push(segment);
      return;
    }

    const words = segment.split(/\s+/);
    let chunk = '';

    words.forEach((word) => {
      const next = chunk ? `${chunk} ${word}` : word;
      if (next.length > 300) {
        if (chunk) mergedSegments.push(chunk);
        chunk = word;
      } else {
        chunk = next;
      }
    });

    if (chunk) mergedSegments.push(chunk);
  });

  return mergedSegments.slice(0, 40);
}

export function SourceSetAsk() {
  const { sourceSetId } = useParams();
  const { user } = useAuth();
  const chatEndRef = useRef<HTMLDivElement>(null);
  const recognitionRef = useRef<any>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const speechSegmentsRef = useRef<string[]>([]);
  const currentSegmentIndexRef = useRef(0);
  const audioStatusRef = useRef<AudioStatus>('idle');
  const speechRateRef = useRef(1);
  const speechTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const voiceAutoAskRef = useRef(false);

  const [sourceSet, setSourceSet] = useState<SourceSet | null>(null);
  const [sources, setSources] = useState<SourceFile[]>([]);
  const [messages, setMessages] = useState<AskMessage[]>([INITIAL_ASK_SOURCE_MESSAGE]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState('');
  const [historySaving, setHistorySaving] = useState(false);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const [speechSupported, setSpeechSupported] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [voiceNotice, setVoiceNotice] = useState('');
  const [audioStatus, setAudioStatus] = useState<AudioStatus>('idle');
  const [activeAudioMessageIndex, setActiveAudioMessageIndex] = useState<number | null>(null);
  const [currentSpeechSegment, setCurrentSpeechSegment] = useState(0);
  const [totalSpeechSegments, setTotalSpeechSegments] = useState(0);
  const [speechRate, setSpeechRate] = useState(1);

  const sourceCountLabel = useMemo(() => {
    const count = sources.length;
    return `${count} source${count === 1 ? '' : 's'}`;
  }, [sources.length]);

  const updateAudioStatus = (status: AudioStatus) => {
    audioStatusRef.current = status;
    setAudioStatus(status);
  };

  const stopSpeech = useCallback(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }

    if (speechTimeoutRef.current) {
      clearTimeout(speechTimeoutRef.current);
      speechTimeoutRef.current = null;
    }

    utteranceRef.current = null;
    speechSegmentsRef.current = [];
    currentSegmentIndexRef.current = 0;
    setCurrentSpeechSegment(0);
    setTotalSpeechSegments(0);
    setActiveAudioMessageIndex(null);
    updateAudioStatus('idle');
  }, []);

  const playSpeechSegment = useCallback((segmentIndex: number, rateOverride?: number) => {
    if (!('speechSynthesis' in window)) return;

    const segments = speechSegmentsRef.current;
    if (!segments.length || segmentIndex < 0 || segmentIndex >= segments.length) {
      stopSpeech();
      return;
    }

    window.speechSynthesis.cancel();

    currentSegmentIndexRef.current = segmentIndex;
    setCurrentSpeechSegment(segmentIndex + 1);
    setTotalSpeechSegments(segments.length);
    updateAudioStatus('playing');

    const utterance = new SpeechSynthesisUtterance(segments[segmentIndex]);
    utteranceRef.current = utterance;

    const voices = window.speechSynthesis.getVoices();
    const preferredVoiceUri = localStorage.getItem('lumina_preferred_voice');
    const voice =
      voices.find((candidate) => candidate.voiceURI === preferredVoiceUri) ||
      voices.find((candidate) => candidate.lang === 'en-GB') ||
      voices.find((candidate) => candidate.lang?.startsWith('en')) ||
      voices[0];

    if (voice) utterance.voice = voice;
    utterance.rate = rateOverride || speechRateRef.current;

    utterance.onend = () => {
      if (utteranceRef.current !== utterance) return;
      if (audioStatusRef.current !== 'playing') return;

      const nextIndex = currentSegmentIndexRef.current + 1;

      if (nextIndex >= speechSegmentsRef.current.length) {
        stopSpeech();
        return;
      }

      speechTimeoutRef.current = setTimeout(() => {
        if (audioStatusRef.current === 'playing') {
          playSpeechSegment(nextIndex);
        }
      }, 250);
    };

    utterance.onerror = (event: any) => {
      if (event?.error === 'canceled' || event?.error === 'interrupted') return;
      if (utteranceRef.current === utterance) {
        stopSpeech();
      }
    };

    window.speechSynthesis.speak(utterance);
  }, [stopSpeech]);

  const playAnswer = useCallback((text: string, messageIndex: number) => {
    if (!('speechSynthesis' in window)) {
      setVoiceNotice('Read aloud is not supported in this browser. You can still read the answer on screen.');
      return;
    }

    const segments = splitAnswerForSpeech(text);
    if (!segments.length) return;

    const event = new CustomEvent('audioConflictStart', { detail: 'ask-source' });
    window.dispatchEvent(event);

    speechSegmentsRef.current = segments;
    currentSegmentIndexRef.current = 0;
    setTotalSpeechSegments(segments.length);
    setCurrentSpeechSegment(1);
    setActiveAudioMessageIndex(messageIndex);
    playSpeechSegment(0);
  }, [playSpeechSegment]);

  const pauseSpeech = () => {
    if (!('speechSynthesis' in window)) return;

    if (window.speechSynthesis.speaking) {
      window.speechSynthesis.pause();
      updateAudioStatus('paused');
    }
  };

  const resumeSpeech = () => {
    if (!('speechSynthesis' in window)) return;

    if (audioStatusRef.current === 'paused') {
      window.speechSynthesis.resume();
      updateAudioStatus('playing');
    }
  };

  const replaySpeech = (text: string, messageIndex: number) => {
    window.speechSynthesis.cancel();
    playAnswer(text, messageIndex);
  };

  const changeSpeechRate = (direction: 'slower' | 'faster') => {
    const nextRate = direction === 'faster'
      ? Math.min(1.6, Number((speechRateRef.current + 0.1).toFixed(1)))
      : Math.max(0.7, Number((speechRateRef.current - 0.1).toFixed(1)));

    speechRateRef.current = nextRate;
    setSpeechRate(nextRate);

    if (audioStatusRef.current === 'playing' && speechSegmentsRef.current.length > 0) {
      playSpeechSegment(currentSegmentIndexRef.current, nextRate);
    }
  };

  const goToSpeechSegment = (direction: 'previous' | 'next') => {
    if (!speechSegmentsRef.current.length) return;

    const nextIndex = direction === 'next'
      ? Math.min(speechSegmentsRef.current.length - 1, currentSegmentIndexRef.current + 1)
      : Math.max(0, currentSegmentIndexRef.current - 1);

    if (nextIndex === currentSegmentIndexRef.current && audioStatusRef.current === 'playing') return;

    playSpeechSegment(nextIndex);
  };

  const loadSourceSet = useCallback(async () => {
    if (!sourceSetId || !user?.uid) return;

    setLoading(true);
    setError('');

    try {
      const data = await getSourceSet(sourceSetId);

      if (!data) {
        setError('Source Set was not found.');
        setSourceSet(null);
        setSources([]);
        return;
      }

      if (data.ownerId !== user.uid) {
        setError('You do not have access to this Source Set.');
        setSourceSet(null);
        setSources([]);
        return;
      }

      const sourceData = await listSourceSetFiles(sourceSetId);
      const savedMessages = await loadAskSourceConversation(sourceSetId, user.uid);

      setSourceSet(data);
      setSources(sourceData);
      setMessages(savedMessages.length > 0 ? [INITIAL_ASK_SOURCE_MESSAGE, ...savedMessages] : [INITIAL_ASK_SOURCE_MESSAGE]);
    } catch (err: any) {
      console.error('Failed to load Ask Source workspace:', err);
      setError(err?.message || 'Failed to load Ask Source workspace.');
    } finally {
      setLoading(false);
    }
  }, [sourceSetId, user?.uid]);

  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    setVoiceSupported(Boolean(SpeechRecognition));
    setSpeechSupported('speechSynthesis' in window);

    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-GB';

      recognitionRef.current.onresult = (event: any) => {
        const transcript = event.results?.[0]?.[0]?.transcript || '';
        const question = transcript.trim();

        if (question) {
          setInput(question);
          setVoiceNotice('Voice captured. Sending your question to Ask Source...');
          voiceAutoAskRef.current = true;

          setTimeout(() => {
            handleAsk(question, { autoPlayAnswer: true });
          }, 300);
        }

        setIsListening(false);
      };

      recognitionRef.current.onerror = (event: any) => {
        if (event.error === 'no-speech') {
          setVoiceNotice('No speech was detected. Please try again or type your question.');
        } else if (event.error === 'not-allowed') {
          setVoiceNotice('Microphone permission was blocked. You can still type your question.');
        } else {
          setVoiceNotice('Voice input could not complete. You can still type your question.');
        }
        setIsListening(false);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }

    return () => {
      try {
        recognitionRef.current?.stop?.();
      } catch {
        // ignore cleanup errors
      }
      stopSpeech();
    };
  }, [stopSpeech]);

  useEffect(() => {
    loadSourceSet();
  }, [loadSourceSet]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, asking]);

  const startListening = () => {
    if (!recognitionRef.current) {
      setVoiceNotice('Voice input is not supported in this browser. Please type your question.');
      return;
    }

    try {
      setVoiceNotice('Listening. Speak your question clearly.');
      setIsListening(true);
      recognitionRef.current.start();
    } catch (err) {
      console.error('Failed to start Ask Source voice input:', err);
      setIsListening(false);
      setVoiceNotice('Voice input could not start. Please type your question.');
    }
  };

  const stopListening = () => {
    try {
      recognitionRef.current?.stop?.();
    } catch {
      // ignore stop errors
    }
    setIsListening(false);
  };

  const handleAsk = async (starter?: string, options?: { autoPlayAnswer?: boolean }) => {
    const question = String(starter || input || '').trim();

    if (!question || !sourceSetId) {
      return;
    }

    stopSpeech();

    const previousMessages = messages;
    const assistantMessageIndex = previousMessages.length + 1;
    const userMessage: AskMessage = { role: 'user', text: question };

    setMessages((current) => [...current, userMessage]);
    setInput('');
    setAsking(true);
    setError('');
    setVoiceNotice('');

    try {
      const result = await askSourceKnowledge(
        sourceSetId,
        question,
        previousMessages
          .filter((message) => message.role === 'user' || message.role === 'assistant')
          .slice(-6)
          .map((message) => ({ role: message.role, text: message.text })),
        sourceSet?.organisationId || null
      );

      const assistantMessage: AskMessage = {
        role: 'assistant',
        text: result.answer,
        citations: result.citations || [],
        groundingStatus: result.groundingStatus
      };

      setMessages((current) => [...current, assistantMessage]);

      setHistorySaving(true);
      try {
        if (user?.uid) {
          await saveAskSourceTurn({
            sourceSetId,
            userId: user.uid,
            question,
            answer: result.answer,
            citations: result.citations || [],
            groundingStatus: result.groundingStatus
          });
        }
      } catch (historyError) {
        console.warn('[AskSourceHistory] Failed to save conversation:', historyError);
      } finally {
        setHistorySaving(false);
      }

      if (options?.autoPlayAnswer) {
        setTimeout(() => {
          playAnswer(result.answer, assistantMessageIndex);
        }, 500);
      }
    } catch (err: any) {
      console.error('Ask Source failed:', err);
      setError(getFriendlyErrorMessage(err, 'ask_source', 'Ask Source failed.'));
      setMessages((current) => [
        ...current,
        {
          role: 'assistant',
          text: getAssistantFailureMessage(err, 'ask_source'),
          groundingStatus: 'insufficient_evidence'
        }
      ]);
    } finally {
      setAsking(false);
    }
  };

  const handleClearConversation = async () => {
    if (!sourceSetId || !user?.uid) return;

    stopSpeech();
    setError('');
    setVoiceNotice('');

    try {
      setHistorySaving(true);
      await clearAskSourceConversation(sourceSetId, user.uid);
      setMessages([INITIAL_ASK_SOURCE_MESSAGE]);
      setVoiceNotice('Conversation history cleared. You can start a new Ask Source conversation.');
    } catch (historyError: any) {
      console.error('[AskSourceHistory] Failed to clear conversation:', historyError);
      setError(historyError?.message || 'Failed to clear Ask Source conversation history.');
    } finally {
      setHistorySaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-main text-text-main flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-9 h-9 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-text-alt">Loading Ask Source...</p>
        </div>
      </div>
    );
  }

  if (!sourceSet) {
    return (
      <div className="min-h-screen bg-bg-main text-text-main px-4 py-8">
        <div className="max-w-3xl mx-auto bg-bg-alt border border-border-main rounded-3xl p-8">
          <AlertTriangle className="w-10 h-10 text-rose-500 mb-4" />
          <h1 className="text-2xl font-bold mb-2">Ask Source unavailable</h1>
          <p className="text-text-alt mb-6">{error || 'The requested Source Set could not be loaded.'}</p>
          <Link to="/source-sets" className="inline-flex items-center gap-2 text-primary font-semibold">
            <ArrowLeft className="w-4 h-4" />
            Back to Source Sets
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-main text-text-main px-4 py-8">
      <div className="max-w-6xl mx-auto">
        <Link to={`/source-sets/${sourceSet.sourceSetId}`} className="inline-flex items-center gap-2 text-text-alt hover:text-primary mb-6">
          <ArrowLeft className="w-4 h-4" />
          Back to Source Set
        </Link>

        <div className="bg-bg-alt border border-border-main rounded-3xl p-6 md:p-8 mb-6">
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-5">
            <div>
              <p className="text-sm font-semibold text-primary uppercase tracking-wide">Knowledge Assistant</p>
              <h1 className="text-3xl font-bold mt-2">{sourceSet.title}</h1>
              <p className="text-text-alt mt-3 max-w-3xl">
                Ask questions grounded only in this Source Set. Type a question, or use Voice Conversation to speak a question and hear the answer aloud.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full bg-bg-main border border-border-main">
                <BookOpen className="w-4 h-4 text-primary" />
                {sourceCountLabel}
              </span>
              <span className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full bg-bg-main border border-border-main">
                <ShieldCheck className="w-4 h-4 text-primary" />
                Source bounded
              </span>
              <span className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-full bg-bg-main border border-border-main">
                <Volume2 className="w-4 h-4 text-primary" />
                Voice conversation
              </span>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-rose-400 whitespace-pre-line">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <aside className="lg:col-span-1 bg-bg-alt border border-border-main rounded-3xl p-5 h-fit">
            <h2 className="font-bold text-lg mb-3">Sources</h2>
            {sources.length === 0 ? (
              <p className="text-sm text-text-alt">No sources are available yet.</p>
            ) : (
              <div className="space-y-3">
                {sources.map((source) => (
                  <div key={source.fileId} className="p-3 rounded-2xl bg-bg-main border border-border-main">
                    <div className="flex items-start gap-2">
                      <FileText className="w-4 h-4 text-primary mt-1 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold break-words">{source.title || source.originalFileName}</p>
                        <p className="text-xs text-text-alt mt-1 uppercase">{source.fileType}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-5 p-4 rounded-2xl bg-bg-main border border-border-main">
              <h3 className="font-semibold text-sm mb-2">Ask Source V1</h3>
              <p className="text-xs text-text-alt leading-relaxed">
                Answers are generated from selected Source Set evidence. Verify cited material before using it for professional or operational decisions.
              </p>
            </div>

            <div className="mt-5 p-4 rounded-2xl bg-bg-main border border-border-main">
              <h3 className="font-semibold text-sm mb-2">Voice note</h3>
              <p className="text-xs text-text-alt leading-relaxed">
                Voice input uses your browser's speech recognition capability. Typed input is always available.
              </p>
              <p className="text-xs text-text-alt leading-relaxed mt-2">
                Voice input: {voiceSupported ? 'Supported' : 'Not supported in this browser'}.
              </p>
              <p className="text-xs text-text-alt leading-relaxed">
                Read aloud: {speechSupported ? 'Supported' : 'Not supported in this browser'}.
              </p>
            </div>

            <div className="mt-5 p-4 rounded-2xl bg-bg-main border border-border-main">
              <h3 className="font-semibold text-sm mb-2">Conversation history</h3>
              <p className="text-xs text-text-alt leading-relaxed mb-3">
                Ask Source saves this workspace conversation so you can return to previous questions and answers.
              </p>
              <button
                type="button"
                onClick={handleClearConversation}
                disabled={asking || historySaving}
                className="w-full px-3 py-2 rounded-xl border border-border-main bg-bg-alt text-xs font-bold text-text-main hover:bg-bg-alt/80 disabled:opacity-50"
              >
                {historySaving ? 'Updating history...' : 'Start new conversation'}
              </button>
            </div>
          </aside>

          <main className="lg:col-span-3 bg-bg-alt border border-border-main rounded-3xl overflow-hidden">
            <div className="h-[62vh] overflow-y-auto p-5 md:p-6 space-y-5">
              {messages.map((message, index) => {
                const isAssistant = message.role === 'assistant';
                const isActiveAudio = activeAudioMessageIndex === index;

                return (
                  <div key={`${message.role}-${index}`} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[88%] rounded-3xl px-5 py-4 ${
                      message.role === 'user'
                        ? 'bg-primary text-white'
                        : 'bg-bg-main border border-border-main text-text-main'
                    }`}>
                      {isAssistant && message.groundingStatus && (
                        <div className="mb-3">
                          <span className="text-xs px-3 py-1 rounded-full bg-bg-alt border border-border-main text-text-alt">
                            {getGroundingLabel(message.groundingStatus)}
                          </span>
                        </div>
                      )}

                      <div className="prose prose-invert prose-sm max-w-none">
                        <Markdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                          {message.text}
                        </Markdown>
                      </div>

                      {isAssistant && (
                        <div className="mt-4 rounded-2xl border border-border-main bg-bg-alt/70 p-3">
                          <div className="flex flex-wrap items-center gap-2">
                            {(!isActiveAudio || audioStatus === 'idle') && (
                              <button
                                type="button"
                                onClick={() => playAnswer(message.text, index)}
                                disabled={!speechSupported}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-primary text-white text-xs font-bold disabled:opacity-50"
                              >
                                <Play className="w-4 h-4" />
                                Play
                              </button>
                            )}

                            {isActiveAudio && audioStatus === 'playing' && (
                              <button
                                type="button"
                                onClick={pauseSpeech}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-bg-main border border-border-main text-xs font-bold"
                              >
                                <Pause className="w-4 h-4" />
                                Pause
                              </button>
                            )}

                            {isActiveAudio && audioStatus === 'paused' && (
                              <button
                                type="button"
                                onClick={resumeSpeech}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-primary text-white text-xs font-bold"
                              >
                                <Play className="w-4 h-4" />
                                Resume
                              </button>
                            )}

                            <button
                              type="button"
                              onClick={stopSpeech}
                              disabled={!isActiveAudio || audioStatus === 'idle'}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-bg-main border border-border-main text-xs font-bold disabled:opacity-50"
                            >
                              <Square className="w-4 h-4" />
                              Stop
                            </button>

                            <button
                              type="button"
                              onClick={() => replaySpeech(message.text, index)}
                              disabled={!speechSupported}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-bg-main border border-border-main text-xs font-bold disabled:opacity-50"
                            >
                              <RotateCcw className="w-4 h-4" />
                              Replay
                            </button>

                            <button
                              type="button"
                              onClick={() => goToSpeechSegment('previous')}
                              disabled={!isActiveAudio || totalSpeechSegments <= 1}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-bg-main border border-border-main text-xs font-bold disabled:opacity-50"
                            >
                              <SkipBack className="w-4 h-4" />
                              Previous
                            </button>

                            <button
                              type="button"
                              onClick={() => goToSpeechSegment('next')}
                              disabled={!isActiveAudio || totalSpeechSegments <= 1}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-bg-main border border-border-main text-xs font-bold disabled:opacity-50"
                            >
                              <SkipForward className="w-4 h-4" />
                              Next
                            </button>

                            <button
                              type="button"
                              onClick={() => changeSpeechRate('slower')}
                              disabled={!speechSupported || speechRate <= 0.7}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-bg-main border border-border-main text-xs font-bold disabled:opacity-50"
                            >
                              Slower
                            </button>

                            <button
                              type="button"
                              onClick={() => changeSpeechRate('faster')}
                              disabled={!speechSupported || speechRate >= 1.6}
                              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-bg-main border border-border-main text-xs font-bold disabled:opacity-50"
                            >
                              Faster
                            </button>
                          </div>

                          <p className="mt-2 text-[11px] text-text-alt">
                            {isActiveAudio && totalSpeechSegments > 0
                              ? `Reading segment ${currentSpeechSegment} of ${totalSpeechSegments} at ${speechRate.toFixed(1)}x speed.`
                              : 'Read this answer aloud, then pause, replay, change speed, or move between segments.'}
                          </p>
                        </div>
                      )}

                      {isAssistant && message.citations && message.citations.length > 0 && (
                        <div className="mt-4 space-y-3">
                          <p className="text-xs font-bold text-text-alt uppercase tracking-wide">Source references</p>
                          {message.citations.map((citation) => (
                            <div key={citation.citationId} className="p-3 rounded-2xl bg-bg-alt border border-border-main">
                              <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="text-xs font-bold text-primary">{citation.citationId}</span>
                                <span className="text-xs text-text-alt">{citation.sourceName}</span>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-bg-main border border-border-main uppercase">
                                  {citation.sourceType}
                                </span>
                              </div>
                              <p className="text-xs text-text-alt leading-relaxed">
                                {citation.excerpt}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {asking && (
                <div className="flex justify-start">
                  <div className="rounded-3xl px-5 py-4 bg-bg-main border border-border-main text-text-alt inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Checking Source Set evidence...
                  </div>
                </div>
              )}

              <div ref={chatEndRef} />
            </div>

            <div className="border-t border-border-main p-4 bg-bg-main/40">
              <div className="flex flex-wrap gap-2 mb-3">
                {[
                  'What are the main points in this Source Set?',
                  'What does this Source Set say about the key process?',
                  'What evidence supports the main recommendation?'
                ].map((starter) => (
                  <button
                    key={starter}
                    type="button"
                    onClick={() => handleAsk(starter)}
                    disabled={asking || sources.length === 0}
                    className="text-xs px-3 py-2 rounded-full border border-border-main bg-bg-alt text-text-alt hover:text-text-main disabled:opacity-50"
                  >
                    {starter}
                  </button>
                ))}
              </div>

              {voiceNotice && (
                <div className="mb-3 rounded-2xl border border-primary/20 bg-primary/10 px-4 py-3 text-xs text-text-alt">
                  {voiceNotice}
                </div>
              )}

              {historySaving && (
                <div className="mb-3 rounded-2xl border border-border-main bg-bg-alt px-4 py-3 text-xs text-text-alt">
                  Saving Ask Source conversation history...
                </div>
              )}

              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  handleAsk();
                }}
                className="flex flex-col sm:flex-row gap-3"
              >
                <input
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  disabled={asking || sources.length === 0}
                  placeholder={sources.length === 0 ? 'Add sources before asking questions.' : 'Ask a question about this Source Set...'}
                  className="flex-1 px-4 py-4 bg-bg-alt border border-border-main rounded-2xl text-text-main placeholder-text-alt/50 focus:ring-2 focus:ring-primary focus:border-primary transition-colors disabled:opacity-50"
                />

                <button
                  type="button"
                  onClick={isListening ? stopListening : startListening}
                  disabled={asking || sources.length === 0 || !voiceSupported}
                  className={`inline-flex items-center justify-center gap-2 px-5 py-4 rounded-2xl font-bold transition-opacity disabled:opacity-50 disabled:cursor-not-allowed ${
                    isListening
                      ? 'bg-rose-600 text-white'
                      : 'bg-bg-alt border border-border-main text-text-main hover:bg-bg-alt/80'
                  }`}
                  title={voiceSupported ? 'Start voice conversation' : 'Voice input is not supported in this browser'}
                >
                  {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  {isListening ? 'Stop listening' : 'Speak and ask'}
                </button>

                <button
                  type="submit"
                  disabled={asking || !input.trim() || sources.length === 0}
                  className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-primary text-white font-bold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
                >
                  {asking ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  Ask
                </button>
              </form>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
